import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, PenTool, Sparkles, Heart, Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
type Props = {
  open: boolean;
  onClose: () => void;
};
const RECIPIENT = 'ferbystore4@gmail.com';
type FormState = {
  fullName: string;
  email: string;
  phone: string;
  productType: string;
  occasion: string;
  theme: string;
  colors: string;
  personalization: string;
  budget: string;
  deadline: string;
  additionalNotes: string;
};
const INITIAL: FormState = {
  fullName: '',
  email: '',
  phone: '',
  productType: '',
  occasion: '',
  theme: '',
  colors: '',
  personalization: '',
  budget: '',
  deadline: '',
  additionalNotes: ''
};
export function CustomOrderModal({
  open,
  onClose
}: Props) {
  const {
    t
  } = useTranslation();
  const c = t.customOrder;
  const f = c.fields;
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  // Body scroll lock + esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [open, onClose]);
  // Reset state when reopened
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setError(false);
    }
  }, [open]);
  const update = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({
      ...prev,
      [k]: e.target.value
    }));
    if (error) setError(false);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim() || !form.productType.trim()) {
      setError(true);
      return;
    }
    const lines = [`${f.fullName.label}: ${form.fullName}`, `${f.email.label}: ${form.email}`, form.phone && `${f.phone.label}: ${form.phone}`, '', `${f.productType.label}: ${form.productType}`, form.occasion && `${f.occasion.label}: ${form.occasion}`, form.theme && `${f.theme.label}: ${form.theme}`, form.colors && `${f.colors.label}: ${form.colors}`, form.personalization && `${f.personalization.label}: ${form.personalization}`, form.budget && `${f.budget.label}: ${form.budget}`, form.deadline && `${f.deadline.label}: ${form.deadline}`, '', form.additionalNotes && `${f.additionalNotes.label}:\n${form.additionalNotes}`].filter(Boolean) as string[];
    const body = lines.join('\n');
    const mailto = `mailto:${RECIPIENT}?subject=${encodeURIComponent(c.defaultSubject)}&body=${encodeURIComponent(body)}`;
    // Trigger user's email client
    window.location.href = mailto;
    setSubmitted(true);
  };
  return <AnimatePresence data-id="element-150">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" aria-modal="true" role="dialog" aria-labelledby="custom-order-title" data-id="element-151">
          <motion.button type="button" aria-label={c.close} onClick={onClose} className="absolute inset-0 bg-lavender-900/50 backdrop-blur-sm cursor-default" data-id="element-152" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-lavender-100" data-id="element-153">
            {/* Header */}
            <div className="flex-shrink-0 px-6 sm:px-8 pt-6 pb-5 border-b border-lavender-100 bg-gradient-to-r from-cream-50 to-lavender-50 flex items-start justify-between gap-4" data-id="element-154">
              <div className="flex items-start gap-3" data-id="element-155">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-lavender-100 flex-shrink-0" data-id="element-156">
                  <PenTool className="w-5 h-5 text-lavender-600" data-id="element-157" />
                </div>
                <div data-id="element-158">
                  <h2 id="custom-order-title" className="font-serif text-2xl text-gray-900 leading-tight" data-id="element-159">
                    {c.title}
                  </h2>
                  <p className="font-script text-base text-lavender-600 mt-0.5" data-id="element-160">
                    {c.subtitle}
                  </p>
                </div>
              </div>
              <button onClick={onClose} aria-label={c.close} className="w-9 h-9 rounded-full bg-white hover:bg-lavender-100 flex items-center justify-center shadow-sm border border-lavender-100 transition-colors flex-shrink-0" data-id="element-161">
                <X className="w-4 h-4 text-gray-600" data-id="element-162" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6" data-id="element-163">
              {submitted ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} className="text-center py-10" data-id="element-164">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-lavender-100 mb-4" data-id="element-165">
                    <CheckCircle2 className="w-8 h-8 text-lavender-600" data-id="element-166" />
                  </div>
                  <h3 className="font-serif text-2xl text-gray-900 mb-3" data-id="element-167">
                    {c.successTitle}
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto leading-relaxed" data-id="element-168">
                    {c.successBody}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-50 border border-lavender-100 text-sm text-gray-600" data-id="element-169">
                    <Mail className="w-4 h-4 text-lavender-500" data-id="element-170" />
                    <span className="font-medium" data-id="element-171">{RECIPIENT}</span>
                  </div>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-172">
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-173">
                    {c.intro}
                  </p>

                  {/* Email info banner */}
                  <div className="bg-cream-50 border border-lavender-100 rounded-xl p-3 flex items-center gap-3 text-sm" data-id="element-174">
                    <Mail className="w-4 h-4 text-lavender-500 flex-shrink-0" data-id="element-175" />
                    <span className="text-gray-700" data-id="element-176">
                      <span className="text-gray-500" data-id="element-177">To: </span>
                      <span className="font-medium" data-id="element-178">{RECIPIENT}</span>
                    </span>
                  </div>

                  {error && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-blush-50 border border-blush-200 rounded-xl p-3 flex items-start gap-3" data-id="element-179">
                      <AlertCircle className="w-5 h-5 text-blush-600 flex-shrink-0 mt-0.5" data-id="element-180" />
                      <div className="text-sm" data-id="element-181">
                        <p className="font-medium text-gray-900" data-id="element-182">
                          {c.errorTitle}
                        </p>
                        <p className="text-gray-600" data-id="element-183">{c.errorBody}</p>
                      </div>
                    </motion.div>}

                  {/* Personal info row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-184">
                    <Field label={f.fullName.label} required requiredText={c.required} data-id="element-185">
                      <input type="text" required value={form.fullName} onChange={update('fullName')} placeholder={f.fullName.placeholder} className={inputClass} data-id="element-186" />
                    </Field>
                    <Field label={f.email.label} required requiredText={c.required} data-id="element-187">
                      <input type="email" required value={form.email} onChange={update('email')} placeholder={f.email.placeholder} className={inputClass} data-id="element-188" />
                    </Field>
                  </div>

                  <Field label={f.phone.label} data-id="element-189">
                    <input type="tel" value={form.phone} onChange={update('phone')} placeholder={f.phone.placeholder} className={inputClass} data-id="element-190" />
                  </Field>

                  <Field label={f.productType.label} required requiredText={c.required} data-id="element-191">
                    <select required value={form.productType} onChange={update('productType')} className={`${inputClass} bg-white`} data-id="element-192">
                      <option value="" data-id="element-193">{f.productType.placeholder}</option>
                      {f.productTypeOptions.map(opt => <option key={opt} value={opt} data-id="element-194">
                          {opt}
                        </option>)}
                    </select>
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-195">
                    <Field label={f.occasion.label} data-id="element-196">
                      <input type="text" value={form.occasion} onChange={update('occasion')} placeholder={f.occasion.placeholder} className={inputClass} data-id="element-197" />
                    </Field>
                    <Field label={f.theme.label} data-id="element-198">
                      <input type="text" value={form.theme} onChange={update('theme')} placeholder={f.theme.placeholder} className={inputClass} data-id="element-199" />
                    </Field>
                  </div>

                  <Field label={f.colors.label} data-id="element-200">
                    <input type="text" value={form.colors} onChange={update('colors')} placeholder={f.colors.placeholder} className={inputClass} data-id="element-201" />
                  </Field>

                  <Field label={f.personalization.label} data-id="element-202">
                    <input type="text" value={form.personalization} onChange={update('personalization')} placeholder={f.personalization.placeholder} className={inputClass} data-id="element-203" />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-204">
                    <Field label={f.budget.label} data-id="element-205">
                      <input type="text" value={form.budget} onChange={update('budget')} placeholder={f.budget.placeholder} className={inputClass} data-id="element-206" />
                    </Field>
                    <Field label={f.deadline.label} data-id="element-207">
                      <input type="text" value={form.deadline} onChange={update('deadline')} placeholder={f.deadline.placeholder} className={inputClass} data-id="element-208" />
                    </Field>
                  </div>

                  <Field label={f.additionalNotes.label} data-id="element-209">
                    <textarea value={form.additionalNotes} onChange={update('additionalNotes')} placeholder={f.additionalNotes.placeholder} rows={4} className={`${inputClass} resize-none`} data-id="element-210" />
                  </Field>

                  <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-medium rounded-full text-white bg-lavender-500 hover:bg-lavender-600 shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5" data-id="element-211">
                    <Sparkles className="w-5 h-5" data-id="element-212" />
                    {c.submitButton}
                  </button>
                </form>}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 sm:px-8 py-4 border-t border-lavender-100 bg-cream-50 flex items-center justify-center gap-2" data-id="element-213">
              <Heart className="w-3.5 h-3.5 text-blush-500 fill-blush-500" data-id="element-214" />
              <p className="text-xs text-gray-500" data-id="element-215">{t.footer.tagline}</p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
const inputClass = 'w-full px-4 py-2.5 text-[15px] rounded-xl border border-lavender-100 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-lavender-400 focus:ring-2 focus:ring-lavender-100 transition-all';
function Field({
  label,
  required,
  requiredText,
  children
}: {
  label: string;
  required?: boolean;
  requiredText?: string;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-216">
      <span className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2" data-id="element-217">
        {label}
        {required && <span className="text-[10px] uppercase tracking-wider text-lavender-600 bg-lavender-50 px-1.5 py-0.5 rounded-full" data-id="element-218">
            {requiredText}
          </span>}
      </span>
      {children}
    </label>;
}